import { FormEvent, useEffect, useState } from 'react'
import { RefreshCw, RotateCcw, ShieldAlert } from 'lucide-react'
import { api } from '../lib/api'
import { can } from '../lib/permissions'
import type { FeishuCardAction, Page, Session } from '../types'
import { EmptyState } from './EmptyState'

const statusLabel: Record<FeishuCardAction['status'], string> = {
  PROCESSING: '处理中', FAILED_RETRYABLE: '等待自动恢复', DEAD_LETTER: '需管理员处理',
  REJECTED: '业务拒绝', COMPLETED: '已完成',
}

export function FeishuCardActionsPanel() {
  const [session, setSession] = useState<Session>()
  const [data, setData] = useState<Page<FeishuCardAction>>()
  const [status, setStatus] = useState('')
  const [keyword, setKeyword] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function load(nextStatus = status) {
    setLoading(true); setError('')
    try {
      const current = session || await api.session()
      setSession(current)
      if (can(current, 'NOTIFICATION_OUTBOX_REPLAY')) setData(await api.feishuCardActions(nextStatus, keyword))
    } catch (value) { setError(value instanceof Error ? value.message : '卡片动作加载失败') }
    finally { setLoading(false) }
  }

  useEffect(() => { void load() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  if (session && !can(session, 'NOTIFICATION_OUTBOX_REPLAY')) return null

  async function act(action: () => Promise<unknown>) {
    setSaving(true); setError('')
    try { await action(); await load() }
    catch (value) { setError(value instanceof Error ? value.message : '卡片动作恢复失败') }
    finally { setSaving(false) }
  }

  function search(event: FormEvent) { event.preventDefault(); void load() }
  return <section className="panel feishu-card-action-panel">
    <div className="panel-heading"><div><h3>飞书卡片动作恢复</h3><p>租约到期会自动接管；系统失败可重试，业务已完成时按权威任务状态对账并修复原卡片。</p></div><button className="button button--ghost" disabled={saving || loading} onClick={() => void act(() => api.reconcileFeishuCardActions())}><RefreshCw size={15} />对账并恢复</button></div>
    <div className="feishu-card-action-filters"><select value={status} onChange={event => { setStatus(event.target.value); void load(event.target.value) }}><option value="">全部状态</option><option value="PROCESSING">处理中</option><option value="FAILED_RETRYABLE">等待自动恢复</option><option value="DEAD_LETTER">需管理员处理</option><option value="REJECTED">业务拒绝</option><option value="COMPLETED">已完成</option></select><form onSubmit={search}><input value={keyword} onChange={event => setKeyword(event.target.value)} placeholder="动作事件、任务或操作人" /><button className="button button--ghost">查询</button></form></div>
    {error && <div className="error-banner">{error}</div>}
    {loading ? <div className="feishu-card-action-empty">正在加载动作状态…</div> : data?.content.length ? <div className="feishu-card-action-list">{data.content.map(item => {
      const recoverable = item.status === 'FAILED_RETRYABLE' || item.status === 'DEAD_LETTER'
        || item.status === 'PROCESSING' && (!item.leaseUntil || new Date(item.leaseUntil).getTime() <= Date.now())
      return <article key={item.eventId}><div className="feishu-card-action-title"><span className={`outbox-state outbox-state--${item.status.toLowerCase()}`}>{statusLabel[item.status]}</span><b>{item.actionType}</b><code>{item.eventId}</code></div><dl><div><dt>业务任务</dt><dd>{item.taskEventType || '—'} · <code>{item.businessKey || item.taskId}</code></dd></div><div><dt>执行信息</dt><dd>{item.username} · 第 {item.attemptCount} 次{item.replayCount ? ` · 人工重放 ${item.replayCount} 次` : ''}</dd></div><div><dt>租约 / 下次恢复</dt><dd>{formatDate(item.leaseUntil || item.nextAttemptAt)}</dd></div><div><dt>结果</dt><dd>{item.lastError || item.resultSummary || '等待动作完成'}</dd></div></dl><div className="feishu-card-action-actions">{item.failureClass && <small><ShieldAlert size={13} />{item.failureClass}</small>}<button className="button button--ghost" disabled={!recoverable || saving} onClick={() => void act(() => api.replayFeishuCardAction(item.eventId))}><RotateCcw size={14} />管理员重放</button></div></article>
    })}</div> : <EmptyState title="暂无卡片动作记录" detail="收到飞书交互式卡片回调后会在这里显示租约、失败分类和恢复结果。" />}
  </section>
}

function formatDate(value?: string) { return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '—' }
